import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  canAssignRepoEngineer,
  canAssignRepoMaintainer,
  canAssignRepoOwner,
} from '@infras/shared-types';
import { PersonCombo, type PersonOption } from './PersonCombo';
import { useMe } from '../lib/useMe';

export interface Owner {
  ownerId: string;
  role: string; // owner | maintainer | engineer
  gitlabUsername?: string;
  displayName?: string;
  email?: string;
}

/** What can carry an owner — mirrors SUBJECT_TYPES in inventory-core. */
export type SubjectType = 'repo' | 'cluster' | 'secret';

// Assignable people = infras-app users (signed in / role-assigned). Readable by
// anyone who may edit (root|maintainer|the subject's Owner); the mutation enforces writes.
const DIRECTORY = gql`
  query AssignableUsers {
    assignableUsers {
      email
      displayName
    }
  }
`;
const OWNER_FIELDS = `ownerId role gitlabUsername displayName email`;
// The polymorphic mutations return the subject's NEW owner list rather than the
// subject itself: "a repo or a cluster or a secret" would be a union the client
// has to discriminate, and the list is the only thing this block re-renders from.
const SET_OWNER = gql`
  mutation SetOwner($subjectType: String!, $subjectId: String!, $role: String!, $email: String!) {
    setOwner(subjectType: $subjectType, subjectId: $subjectId, role: $role, email: $email) { ${OWNER_FIELDS} }
  }
`;
// `email` removes ONE holder. Maintainer and Engineer are rosters, so clearing the
// whole role because someone pressed × on one chip would be a data-loss bug.
const CLEAR_OWNER = gql`
  mutation ClearOwner($subjectType: String!, $subjectId: String!, $role: String!, $email: String) {
    clearOwner(subjectType: $subjectType, subjectId: $subjectId, role: $role, email: $email) { ${OWNER_FIELDS} }
  }
`;

const label = (o: { displayName?: string; gitlabUsername?: string; email?: string }) =>
  o.displayName || o.gitlabUsername || o.email || '—';

/** The Owner of a list of owners, or undefined. Exported because the inventory
 *  tables render the same person in a column without the editing block. */
export const ownerOf = (owners: Owner[] = []) => owners.find((o) => o.role === 'owner');
export const ownerLabel = label;

/**
 * Ownership block for ANY subject — repos, clusters, secrets.
 *   - Owner      → editable by app root|maintainer. ONE person: assigning replaces.
 *   - Maintainer → editable by app root|maintainer OR by this subject's own Owner
 *     (delegation — the owner adds their maintainer afterwards). A ROSTER.
 *   - Engineer   → same rule as Maintainer. A ROSTER: who to pull in for the work.
 *
 * Rosters ADD rather than replace, and × removes the one person you clicked.
 * Not the app "maintainer" access role (Settings → Team), which is a different plane.
 *
 * Was `RepoOwnership`, hardcoded to a GitLab project id. `ownerships` was always
 * polymorphic and the authorization rule asks the same question of every subject
 * ("is the caller this thing's Owner?"), so widening it introduced no new policy.
 */
export function Ownership({
  subjectType,
  subjectId,
  owners,
  onChanged,
}: {
  subjectType: SubjectType;
  /** The row's own `id` — NOT a natural key. Clusters and repos both have one. */
  subjectId: string;
  owners: Owner[];
  onChanged?: () => void;
}) {
  const { me, role } = useMe();

  const owner = owners.find((o) => o.role === 'owner');
  const holders = (r: string) => owners.filter((o) => o.role === r);

  const isSubjectOwner =
    !!owner?.email && !!me?.email && owner.email.toLowerCase() === me.email.toLowerCase();
  const canEditOwner = !!role && canAssignRepoOwner(role);
  const canEditMaintainer = !!role && canAssignRepoMaintainer(role, isSubjectOwner);
  const canEditEngineer = !!role && canAssignRepoEngineer(role, isSubjectOwner);
  const canEditAny = canEditOwner || canEditMaintainer || canEditEngineer;

  const dir = useQuery<{ assignableUsers: { email: string; displayName?: string }[] }>(DIRECTORY, {
    skip: !canEditAny,
  });
  const [setOwner, { loading: saving }] = useMutation(SET_OWNER, { onCompleted: onChanged });
  const [clearOwner] = useMutation(CLEAR_OWNER, { onCompleted: onChanged });

  const opts: PersonOption[] = (dir.data?.assignableUsers ?? [])
    .map((u) => ({ email: u.email, label: u.displayName || u.email }))
    .sort((a, b) => a.label.localeCompare(b.label));

  type Role = 'owner' | 'maintainer' | 'engineer';
  const set = (r: Role, email: string) =>
    void setOwner({ variables: { subjectType, subjectId, role: r, email } });
  /** Pass the email so a roster loses ONE person, not everyone. */
  const clear = (r: Role, email?: string) =>
    void clearOwner({ variables: { subjectType, subjectId, role: r, email: email ?? null } });

  const row = (r: Role, title: string, editable: boolean, single: boolean) => {
    const cur = holders(r);
    return (
      <div className="own-row">
        <div className="own-role">{title}</div>
        <div className="own-person">
          {cur.length ? (
            cur.map((h) => (
              <span className="tag-badge" key={h.ownerId} title={h.email}>
                {label(h)}
                {editable && (
                  <button
                    type="button"
                    className="own-x"
                    title={`remove ${h.email ?? label(h)}`}
                    onClick={() => clear(r, h.email)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          ) : (
            // "unassigned" reads as a blank field; this is a gap someone should
            // close, and for an unowned production cluster it is the finding.
            <span className="cell-meta">
              {editable ? 'nobody yet — assign someone' : 'nobody yet'}
            </span>
          )}
          {editable && (
            // A roster always offers "add…" — the picker is how you grow it, so it
            // must not read as "change" once one person is on.
            <PersonCombo
              placeholder={single ? (cur.length ? 'change…' : 'assign…') : 'add…'}
              disabled={saving}
              options={single ? opts : opts.filter((o) => !cur.some((h) => h.email === o.email))}
              onPick={(email) => set(r, email)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="ownership">
      <h3 className="section">Ownership</h3>
      {row('owner', 'Owner', canEditOwner, true)}
      {row('maintainer', 'Maintainers', canEditMaintainer, false)}
      {row('engineer', 'Engineers', canEditEngineer, false)}
    </div>
  );
}

/** Owner cell for an inventory table — the person, or the gap made visible. */
export function OwnerCell({ owners }: { owners?: Owner[] }) {
  const owner = ownerOf(owners);
  if (!owner) return <span className="cell-meta">unowned</span>;
  return (
    <span className="tag-badge" title={owner.email}>
      {label(owner)}
    </span>
  );
}
